/**
 * PaginaSistema — plantilla única de las páginas de sistema (brand book SOA 10 PRO · §14).
 *
 * Símbolo de 4 cuadrados (26 px, radio 8, gap 6) con UNO encendido y el resto en contorno · título
 * 44 px 900 · texto 18 px · chip con el dato para soporte (mono) · botón principal 50 px radio 15 +
 * secundario opcional. El color del cuadrado encendido comunica el tipo de situación:
 *   ambar   = espera / atención        violeta = falta algo / no es tu sección
 *   verde   = se resuelve con un toque  gris    = problema técnico
 */
const TONOS = {
  ambar:   { luz: "#F59E0B", chipBg: "#FEF3C7", chipFg: "#92400E" },
  violeta: { luz: "#7C3AED", chipBg: "#EDE9FE", chipFg: "#5B21B6" },
  verde:   { luz: "#10B981", chipBg: "#D1FAE5", chipFg: "#065F46" },
  gris:    { luz: "#6B6F7A", chipBg: "#ECEDF1", chipFg: "#3B3E46" },
};

export function SimboloEstados({ tono = "gris", oscuro = false, encendido = 0 }) {
  const t = TONOS[tono] || TONOS.gris;
  const contorno = oscuro ? "#3B3E46" : "#CDD0D7";
  return (
    <div aria-hidden="true" style={{ display: "grid", gridTemplateColumns: "26px 26px", gap: 6 }}>
      {[0, 1, 2, 3].map(i => (
        <span key={i} style={{
          width: 26, height: 26, borderRadius: 8,
          background: i === encendido ? t.luz : "transparent",
          boxShadow: i === encendido ? "none" : `inset 0 0 0 2px ${contorno}`,
        }}/>
      ))}
    </div>
  );
}

export default function PaginaSistema({
  tono = "gris", oscuro = false, encendido = 0,
  titulo, texto, chip, children,
  primario, secundario, pie,
}) {
  const t = TONOS[tono] || TONOS.gris;
  const fondo = oscuro ? "#0B0D10" : "#F2F3F6";
  const tinta = oscuro ? "#FFFFFF" : "#111318";
  const suave = oscuro ? "#A6ABB6" : "#4B4F59";

  const Boton = ({ b, principal }) => {
    if (!b) return null;
    const estilo = {
      height: principal ? 50 : 44, padding: "0 22px", borderRadius: 15, fontSize: 15, fontWeight: 800,
      display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer", border: 0,
      background: principal ? (oscuro ? "#FFFFFF" : "#111318") : "transparent",
      color: principal ? (oscuro ? "#111318" : "#FFFFFF") : tinta,
      boxShadow: principal ? "none" : `inset 0 0 0 2px ${oscuro ? "#3B3E46" : "#CDD0D7"}`,
      textDecoration: "none",
    };
    return b.href
      ? <a href={b.href} style={estilo}>{b.label}</a>
      : <button type="button" onClick={b.onClick} style={estilo}>{b.label}</button>;
  };

  return (
    <div role="main" style={{
      minHeight: "100vh", background: fondo, color: tinta, display: "flex",
      alignItems: "center", justifyContent: "center", padding: "32px 24px",
      fontFamily: "Figtree, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    }}>
      <div style={{ maxWidth: 560, width: "100%" }}>
        <SimboloEstados tono={tono} oscuro={oscuro} encendido={encendido} />
        <h1 style={{ margin: "28px 0 0", fontSize: "clamp(32px, 6vw, 44px)", lineHeight: 1.05, fontWeight: 900, letterSpacing: "-0.03em", textWrap: "balance" }}>
          {titulo}
        </h1>
        {texto && <p style={{ margin: "16px 0 0", fontSize: 18, lineHeight: 1.5, color: suave }}>{texto}</p>}
        {chip && (
          <div style={{ marginTop: 20, display: "inline-block", padding: "6px 12px", borderRadius: 10, background: oscuro ? "#16191F" : t.chipBg, color: oscuro ? "#E7E9EE" : t.chipFg, fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 14 }}>
            {chip}
          </div>
        )}
        {children}
        {(primario || secundario) && (
          <div style={{ marginTop: 28, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <Boton b={primario} principal />
            <Boton b={secundario} />
          </div>
        )}
        {pie && <p style={{ marginTop: 20, fontSize: 14, color: suave }}>{pie}</p>}
      </div>
    </div>
  );
}
